import {
  type BaseType,
  CustomSelector,
  ECustomSelector,
  ESelectModalType,
  SelectModal,
} from '@globalmed/atlas-ui';
import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLanguageContext } from '@/features/LanguageContext';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useListStore } from '@/stores/list';
import type { TListCountriesItemCamel } from '@/types/list';

type TProps = {
  nameCountry?: string;
  nameState?: string;
  required?: boolean;
  stateLabel?: string;
};

/**
 * Renders country and state selection fields integrated with react-hook-form.
 * Supports responsive behavior: SelectModal for small screens and CustomSelector for larger screens.
 * Updates state options based on selected country and handles form validation.
 * @param props - Component props.
 * @param props.nameCountry - Optional name for the country field in the form (default: 'country').
 * @param props.nameState - Optional name for the state field in the form (default: 'state').
 * @param props.required - Indicates whether the fields are required.
 * @param props.stateLabel - Optional translation key for the state field label (default: 'common.state').
 * @returns LocationFields component.
 */
export const LocationFields = ({
  nameCountry = 'country',
  nameState = 'state',
  required = false,
  stateLabel = 'common.state',
}: TProps) => {
  const {
    control,
    setValue,
    trigger,
    watch,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation();
  const countries = useListStore((state) => state.countries);
  const [stateOptions, setStateOptions] = useState<BaseType[]>([]);
  const { language } = useLanguageContext();
  const isSmallScreen = useMediaQuery('(max-width: 576px)');

  const countryValue = watch(nameCountry);

  const itemsCountries = Object.values(countries).map(
    (country: TListCountriesItemCamel) => {
      const title = country.labels[language] || country.labels['en'];
      return {
        id: country.id,
        title,
      };
    },
  );

  const handleCountryChange = (countryId: string) => {
    setValue(nameCountry, countryId);
    const selectedCountry = Object.values(countries).find(
      (item) => item.id === countryId,
    );

    if (selectedCountry?.subdivisions) {
      const newStateOptions = selectedCountry.subdivisions.map((state) => ({
        id: state.id,
        title: state.name,
      }));
      setStateOptions(newStateOptions);
    } else {
      setStateOptions([]);
    }
    setValue(nameState, '');
  };

  useEffect(() => {
    if (countryValue) {
      const selectedCountry = Object.values(countries).find(
        (item) => item.id === countryValue,
      );

      if (selectedCountry?.subdivisions) {
        const newStateOptions = selectedCountry.subdivisions.map((state) => ({
          id: state.id,
          title: state.name,
        }));
        setStateOptions(newStateOptions);
      } else {
        setStateOptions([]);
        setValue(nameState, '');
      }
    } else {
      setStateOptions([]);
      setValue(nameState, '');
    }
  }, [countryValue, countries, setValue, nameState]);

  return (
    <>
      <Controller
        name={nameCountry}
        control={control}
        render={({ field }) =>
          isSmallScreen ? (
            <SelectModal
              type={ESelectModalType.SingleSelect}
              options={itemsCountries}
              selected={field.value}
              onChange={(id) => {
                handleCountryChange(String(id));
                field.onChange(id);
              }}
              labelProps={{
                label: t('common.country'),
                required,
              }}
              error={t(errors[nameCountry]?.message?.toString() || '')}
              placeholder={t('dashboard.select-country')}
              width="100%"
            />
          ) : (
            <CustomSelector
              type={ECustomSelector.Select}
              options={itemsCountries}
              selected={field.value}
              onChange={(id) => {
                handleCountryChange(id);
                field.onChange(id);
              }}
              labelProps={{
                label: t('common.country'),
                required,
              }}
              error={t(errors[nameCountry]?.message?.toString() || '')}
              placeholder={t('dashboard.select-country')}
              enableQuickSearch
              sx={{ width: '100%' }}
            />
          )
        }
      />

      <Controller
        name={nameState}
        control={control}
        render={({ field }) =>
          isSmallScreen ? (
            <SelectModal
              type={ESelectModalType.SingleSelect}
              options={stateOptions}
              selected={field.value}
              onChange={(id) => {
                setValue(nameState, String(id));
                field.onChange(id);
                void trigger(nameState);
              }}
              labelProps={{
                label: t(stateLabel),
                required,
              }}
              error={t(errors[nameState]?.message?.toString() || '')}
              disabled={!countryValue || !stateOptions.length}
              placeholder={t('dashboard.select-state')}
              width="100%"
            />
          ) : (
            <CustomSelector
              type={ECustomSelector.Select}
              options={stateOptions}
              selected={field.value}
              onChange={(id) => {
                setValue(nameState, id);
                field.onChange(id);
                void trigger(nameState);
              }}
              labelProps={{
                label: t(stateLabel),
                required,
              }}
              error={t(errors[nameState]?.message?.toString() || '')}
              disabled={!countryValue || !stateOptions.length}
              placeholder={t('dashboard.select-state')}
              enableQuickSearch
              sx={{ width: '100%' }}
            />
          )
        }
      />
    </>
  );
};
